import React, { useContext, useState } from "react";

import classNames from "classnames";
import { motion } from "framer-motion";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import useDevice from "../../customHooks/useDevice";
import {
  horizontalAlignmentMap,
  verticalAlignmentMap,
} from "../../styles/positionMaps";
import RedirectButton from "../RedirectButton/RedirectButton";
import MuxVideo from "../Video/MuxVideo";
import Video from "../Video/Video";
import HeroSearchForm from "./HeroSearchForm";
import HeroText from "./HeroText";
import VideoModal from "./VideoModal";

const StyledWatchHeroVideoButton = styled.button`
  position: relative;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
  padding: 10px 20px;
  text-transform: uppercase;
  transition: all 0.2s ease-in-out;
  border-radius: 2px;
  min-width: 200px;
  user-select: none;
  border: 1px solid transparent;
`;

const StyledHero = styled.div`
  background-size: cover;
  position: relative;
  background-repeat: no-repeat;

  @media (min-width: ${breakpoints.XS_MOBILE}) {
    background-image: ${({ $alwaysShowImage, $image, $isSearchBox }) =>
      $isSearchBox && !$alwaysShowImage ? "none" : `url(${$image})`};
    min-height: ${({ $mobileMinHeight, $isVideoAdded }) =>
      $isVideoAdded ? "100px" : `${$mobileMinHeight}` || "450px"};
    background-position: ${({ $backgroundPosition }) =>
      `${$backgroundPosition}` || "center"};
  }

  @media (min-width: ${breakpoints.MOBILE}) {
    background-image: ${({ $image }) => `url(${$image})`} !important;
    min-height: ${({ $mobileMinHeight }) => `${$mobileMinHeight}` || "450px"};
    background-position: ${({ $backgroundPosition }) =>
      `${$backgroundPosition}` || "center"};
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    background-image: ${({ $image }) => `url(${$image})`} !important;
    min-height: ${({ $minHeight }) => `${$minHeight}` || "450px"};
    background-position: ${({ $backgroundPosition }) =>
      `${$backgroundPosition}` || "center"};

    ${({ $showOverlay, $overlayBackground }) =>
      $showOverlay &&
      `
    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      background: ${$overlayBackground};
      width: 100%;
      height: 100%;
    }

  `}
  }

  ${(props) => props.$dynamicStyles || ""}
`;

const StyledHeroWrapper = styled.div`
  z-index: 11;
`;

const StyledHeroContent = styled.div`
  min-height: ${({ $minHeight }) => `${$minHeight}`};

  @media (max-width: ${breakpoints.MOBILE}) {
    min-height: ${({ $mobileMinHeight }) => `${$mobileMinHeight}`};
  }
`;

function Hero() {
  const {
    heroImage,
    minHeight,
    mobileMinHeight,
    contentClassName = "w-50",
    backgroundPosition,
    horizontalTextAlignment,
    verticalTextAlignment,
    showOverlay,
    vimeoID,
    mobileVimeoID,
    muxPlaybackId,
    mobileMuxPlaybackId,
    searchBoxComponent,
    redirectComponent,
    overlayBackground = "rgba(0, 0, 0, 0.25)",
    volumeToggle = false,
    alwaysShowImage,
    showVideoModalButton = false,
    videoHeroType = "classic",
    videoModalButtonText = "Watch the video",
    animateContentOnHover = false,
    CSS,
  } = useContext(ComponentContext);
  const { isMobile } = useDevice();

  const { modalStore } = useStores();
  const { openModal, closeModal } = modalStore;
  const heroImageUrl = heroImage?.url;
  const showSearchBox = !isEmpty(searchBoxComponent);
  const showRedirectButton = !isEmpty(redirectComponent);
  const [isHovered, setIsHovered] = useState(false);

  const heroContentClassnames = classNames(
    "d-flex flex-grow-1 py-3 hero-content",
    `justify-content-${
      horizontalAlignmentMap[horizontalTextAlignment || "center"]
    }`,
    `align-items-${verticalAlignmentMap[verticalTextAlignment || "center"]}`
  );

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const getAnimationProps = () => {
    if (!animateContentOnHover) {
      return { initial: "visible", animate: "visible" };
    }
    return {
      initial: "hidden",
      animate: isHovered ? "visible" : "hidden",
    };
  };

  const handleClick = (e) => {
    e.preventDefault();
    openModal({
      content: (
        <VideoModal
          closeModal={closeModal}
          vimeoID={isMobile ? mobileVimeoID : vimeoID}
        />
      ),
      fullscreen: false,
      overflowVisible: true,
      size: "xl",
    });
  };

  const heroContent = (
    <div
      className={`container-xxl ${
        vimeoID ? "position-relative" : "w-100"
      } hero-content-wrapper`}
    >
      <StyledHeroContent
        className={heroContentClassnames}
        data-testid="hero-content"
        $minHeight={minHeight}
        $mobileMinHeight={mobileMinHeight}
      >
        <motion.div
          className={contentClassName}
          variants={containerVariants}
          {...getAnimationProps()}
        >
          <motion.div variants={itemVariants}>
            <HeroText />
          </motion.div>
          {showSearchBox && (
            <motion.div variants={itemVariants}>
              <HeroSearchForm />
            </motion.div>
          )}
          {showRedirectButton && (
            <motion.div variants={itemVariants}>
              <RedirectButton
                customUrl={redirectComponent?.url}
                text={redirectComponent?.buttonText}
                bgColor={redirectComponent?.backgroundColor}
                textColor={redirectComponent?.textColor}
                alignment={redirectComponent?.buttonAlignment}
                backgroundHoverColour={redirectComponent?.buttonHoverColour}
                textHoverColour={redirectComponent?.buttonHoverTextColour}
              />
            </motion.div>
          )}
          {showVideoModalButton ? (
            <motion.div variants={itemVariants}>
              <div
                className="d-flex justify-content-center"
                id="watch-the-video-button"
              >
                <StyledWatchHeroVideoButton
                  onClick={(e) => handleClick(e)}
                  type="button"
                >
                  {videoModalButtonText}
                </StyledWatchHeroVideoButton>
              </div>
            </motion.div>
          ) : null}
        </motion.div>
      </StyledHeroContent>
    </div>
  );

  const currentMuxPlaybackId = isMobile ? mobileMuxPlaybackId : muxPlaybackId;
  const currentVimeoID = isMobile ? mobileVimeoID : vimeoID;
  const hasMuxVideo = !isEmpty(currentMuxPlaybackId);
  const hasVimeoVideo = !isEmpty(currentVimeoID);

  const withMux = (
    <MuxVideo
      isBackgroundVideo
      isPauseable={false}
      isPaused={false}
      loop
      overlayBackground={overlayBackground}
      videoHeroType={videoHeroType}
      playbackId={currentMuxPlaybackId}
      volumeToggle={volumeToggle}
      withOverlay={showOverlay}
      posterUrl={heroImageUrl}
      minHeight={minHeight}
      mobileMinHeight={mobileMinHeight}
    >
      {heroContent}
    </MuxVideo>
  );

  const withVimeo = (
    <Video
      height="100%"
      isBackgroundVideo
      isPauseable={false}
      isPaused={false}
      loop
      overlayBackground={overlayBackground}
      videoHeroType={videoHeroType}
      vimeoID={currentVimeoID}
      volumeToggle={volumeToggle}
      withOverlay={showOverlay}
    >
      {heroContent}
    </Video>
  );

  const isVideoAdded = hasMuxVideo || hasVimeoVideo;
  // eslint-disable-next-line no-nested-ternary
  const hero = hasMuxVideo ? withMux : hasVimeoVideo ? withVimeo : heroContent;

  return (
    <StyledHero
      $alwaysShowImage={alwaysShowImage}
      $backgroundPosition={backgroundPosition}
      className="align-items-center d-flex flex-column position-relative hero-component"
      $dynamicStyles={CSS}
      $image={hasMuxVideo ? "none" : heroImageUrl}
      $isSearchBox={showSearchBox}
      $isVideoAdded={isVideoAdded}
      $minHeight={minHeight}
      $overlayBackground={overlayBackground}
      $showOverlay={showOverlay}
      $mobileMinHeight={mobileMinHeight}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledHeroWrapper className="flex-grow-1 d-flex flex-column w-100">
        {hero}
      </StyledHeroWrapper>
    </StyledHero>
  );
}

export default observer(Hero);
